import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {WHITE} from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';

export default function ParameterOptimizationModelingOverviewView(
  _: MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_OVERVIEW>
): JSX.Element {
  return (
    <div className={css(styles.wrapper)}>
      <Header2>Using this analysis</Header2>
      <p>
        We have built a machine learning model that analyzes how your experimental inputs affect your
        experimental outcome. Use the dashboard to learn more about the effects of these inputs on the
        outcome, and use the interactive prediction to simulate the results of different experimental inputs.
      </p>

      <Header2>Understanding your dashboard</Header2>
      <p>
        The dashboard helps you assess your model’s quality through model metrics and a ranking of parameter
        importance. Use the dashboard to evaluate how the model results compare to your own assumptions about
        your experiments. If the model gives unexpected or poor results, you may need to rerun the analysis
        with more data or use different parameters.
      </p>

      <Header2>Making predictions</Header2>
      <p>
        You can predict an experimental output on the Predictions page after entering new parameter values.
        Certain parameters will not be displayed. Some possible reasons include:
      </p>

      <ol>
        <li>
          <code>id</code> columns used for joins are excluded.
        </li>
        <li>The model may remove inputs if it learns that they do not have predictive power.</li>
      </ol>

      <Header2>Terminology</Header2>
      <ol>
        <li>
          <b>Parameter:</b> A parameter represents a variable in your experiment which can have an impact on
          the output. We recommend using parameters that map to an experimental input that you have control
          over.
        </li>
        <li>
          <b>Output:</b> The output is the dependent variable being optimized, and the value that is predicted
          by the model. Some possible examples could be yield, titer, or assay result.
        </li>
        <li>
          <b>Parameter contribution:</b> The parameter contribution measures how much the parameter’s value
          affects the output. We calculate this with{' '}
          <a href="https://medium.com/dataman-in-ai/explain-your-model-with-the-shap-values-bc36aac4de3d">
            SHAP values
          </a>{' '}
          because they are agnostic to the type of machine learning model that best fits your data.
        </li>
        <li>
          <b>Training & testing:</b> As part of model-building, we randomly split the data 80:20 between
          training data and testing data. Training data "teaches" the ML algorithms the patterns found in the
          data while testing data is used to validate the model performance.
        </li>
      </ol>

      <Header2>Can I provide feedback?</Header2>
      <p>
        The Benchling Machine Learning team would love your feedback! Feel free to email our Product Manager
        at helen.liumayo@benchling.com with any feedback or requests.
      </p>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    background: WHITE,
    marginLeft: '10%',
    marginRight: '10%',
    padding: 20,
    overflowY: 'auto',
  },
});
